// MA-61 slice 1 — data access for teacher class creation + roster.
//
// Deliberately thin: no `students` rows are created here (see the migration
// 0023 header note — reusing `students`/`parent_id` for teacher rosters was
// red-teamed and rejected because `fetchStudents()`/`crossDeviceHydration.js`
// would hydrate a pasted classroom roster into the teacher's OWN kid
// switcher on a fresh-device sign-in). `class_enrollments.student_id` stays
// null until the no-email-student-login sub-slice designs real identity.
import { supabase } from "@/supabaseClient.js";
import { captureSupabaseError } from "@/lib/sentry";
import { generateJoinCode, parseRosterNames } from "./classRoster";
import { runGatedRosterWrite } from "@/App/schoolConsentGate.js";
import type { TeacherClassLane } from "./classLane";

export type SupabaseClient = typeof supabase;

export interface TeacherClass {
  id: string;
  teacher_id: string;
  name: string;
  grade: string | null;
  join_code: string;
  archived: boolean;
  created_at: string;
  // MA-288 kid-secret sub-slice — added by migration 0042. `select("*")`
  // (fetchTeacherClasses) already returns this once 0042 is applied; typed
  // here so callers don't need a cast. Undefined (not false) pre-apply,
  // since a `select("*")` simply omits a column the DB doesn't have yet —
  // callers treat both `undefined` and `false` as "no gate".
  kid_secret_required?: boolean;
}

export interface ClassEnrollment {
  id: string;
  class_id: string;
  student_id: string | null;
  display_name: string;
  joined_at: string;
  // MA-59 slice 2 (claim-flow-ui) — added by migration 0024. `select("*")`
  // already returns these; typed here so the roster UI can read them without
  // a cast. claim_code is the teacher-visible code for an unclaimed row (null
  // until first generate); a non-null revoked_at means any lingering
  // claim_code value is DEAD (the claim RPC's `revoked_at IS NULL` guard
  // rejects it) — never display a code when revoked_at is set.
  claim_code: string | null;
  claimed_by_parent_id: string | null;
  claimed_at: string | null;
  revoked_at: string | null;
  // MA-543 — added by migration 0043. Optional (not required, unlike the
  // four MA-59 columns above): pre-apply, `select("*")` simply omits an
  // unknown column and this key is absent from the object entirely
  // (`undefined`, not `null`) — see 0043's header. `undefined` must behave
  // exactly like `null` everywhere this is read (both mean "no expiry");
  // src/lib/teacher/nextStepsRow.ts's hasLiveClaimCode is the sole reader.
  claim_code_expires_at?: string | null;
  /** MA-925 — the persisted door picture (migration 0059). Absent before the
   *  founder applies 0059; the Students table then shows the initial tile. */
  door_picture?: number | null;
  /** MA-939 — when the teacher removed this child from the class list
   *  (migration 0065). The roster reads omit removed rows, so a row the app
   *  holds carries null here, or no key at all before the founder applies 0065. */
  removed_at?: string | null;
}

/** MA-939 — a roster row counts as live only when neither revoked nor removed
 *  (migration 0065). `undefined` (before 0065) behaves exactly like null. */
export function isRemovedEnrollment(row: { removed_at?: string | null } | null | undefined): boolean {
  return !!row && row.removed_at !== null && row.removed_at !== undefined;
}

/** MA-939 — true when a 42703 answer names `column` (PostgREST's message
 *  "column class_enrollments.removed_at does not exist"), or is a bare 42703
 *  with no column named at all. A 42703 that names a DIFFERENT column is not
 *  this column's absence. */
export function isMissingNamedColumnError(error: unknown, column: string): boolean {
  const e = error as { code?: string; message?: string } | null | undefined;
  const msg = e?.message ?? "";
  if (/column .* does not exist/i.test(msg)) return new RegExp(`column .*\\b${column}\\b.* does not exist`, "i").test(msg);
  return e?.code === "42703";
}

/** Postgres 42P01 = "relation does not exist" — the pre-founder-apply state
 *  for a brand-new migration (0023 not yet run against prod). Callers use
 *  this to show a friendly "not live yet" message instead of a raw error. */
export function isMissingTableError(error: unknown): boolean {
  const code = (error as { code?: string } | null | undefined)?.code;
  return code === "42P01";
}

/** Postgres 42703 = "column does not exist" — the pre-founder-apply state
 *  for a narrowed `select`/`update` naming a migration-0042 column
 *  (`enrollment_id`, `kid_secret_required`) before 0042 is live. Mirrors
 *  `isMissingTableError` (42P01) / claimApi.ts's `isMissingFunctionError`
 *  (PGRST202) — same MA-59 "fail closed to a friendly not-live-yet message"
 *  convention, extended to the column-level PostgREST error shape. Checks
 *  both the raw code and, defensively, the message text naming the column
 *  (PostgREST sometimes surfaces 42703 as a wrapped PGRST-prefixed error
 *  instead of the bare Postgres code, depending on version). */
export function isMissingColumnError(error: unknown, column: string): boolean {
  const e = error as { code?: string; message?: string } | null | undefined;
  if (e?.code === "42703") return true;
  const msg = e?.message ?? "";
  return new RegExp(`column .*${column}.* does not exist`, "i").test(msg);
}

const JOIN_CODE_MAX_ATTEMPTS = 5;

/** Creates a class, retrying on join_code collision (23505 unique_violation
 *  on classes_join_code_unique) up to JOIN_CODE_MAX_ATTEMPTS times — a
 *  6-char code over a 33-char alphabet has a large space, so a collision is
 *  rare, but the constraint makes a retry loop cheap insurance rather than a
 *  user-visible failure. */
export async function createClass(
  teacherId: string,
  input: { name: string; grade?: string | null },
  client: SupabaseClient = supabase,
): Promise<TeacherClass> {
  let lastError: unknown = null;
  for (let attempt = 0; attempt < JOIN_CODE_MAX_ATTEMPTS; attempt++) {
    const { data, error } = await client
      .from("classes")
      .insert({
        teacher_id: teacherId,
        name: input.name,
        grade: input.grade ?? null,
        join_code: generateJoinCode(),
      })
      .select()
      .single();
    if (!error) return data as TeacherClass;
    lastError = error;
    if ((error as { code?: string }).code !== "23505") break;
    // 23505 on this table can only be the join_code unique constraint —
    // retry with a freshly generated code.
  }
  captureSupabaseError("createClass failed", lastError, { teacherId });
  throw lastError;
}

/** A teacher's own classes, oldest first (RLS already scopes this to the
 *  caller's own rows; the explicit filter documents intent). */
export async function fetchTeacherClasses(
  teacherId: string,
  client: SupabaseClient = supabase,
): Promise<TeacherClass[]> {
  const { data, error } = await client
    .from("classes")
    .select("*")
    .eq("teacher_id", teacherId)
    .order("created_at", { ascending: true });
  if (error) {
    captureSupabaseError("fetchTeacherClasses failed", error, { teacherId });
    throw error;
  }
  return (data ?? []) as TeacherClass[];
}

export async function fetchClassRoster(
  classId: string,
  client: SupabaseClient = supabase,
): Promise<ClassEnrollment[]> {
  // MA-939 — a removed child (migration 0065) is not on the roster. The request
  // is today's `select("*")`, unchanged: once 0065 is applied every row carries
  // removed_at and a removed row is dropped below; before it no row carries the
  // column (a `*` select cannot answer 42703), so every row is kept, as today.
  const { data, error } = await client
    .from("class_enrollments")
    .select("*")
    .eq("class_id", classId)
    .order("joined_at", { ascending: true });
  if (error) {
    captureSupabaseError("fetchClassRoster failed", error, { classId });
    throw error;
  }
  return ((data ?? []) as ClassEnrollment[]).filter((row) => !isRemovedEnrollment(row));
}

/** MA-288 classroom-roster-identity (read-only sub-slice) — the columns the
 *  kiosk hand-off door (/play/who) needs, narrowed to exclude `claim_code`.
 *  `claim_code` is a bearer secret: anyone holding it can link ANY parent
 *  account to that enrollment row (see 0024_class_enrollment_claim.sql's
 *  `claim_class_enrollment`). `fetchClassRoster` above is fine for the
 *  teacher-authenticated `/teacher` roster screen (which legitimately
 *  displays/regenerates the code), but a kiosk device is handed to kids in a
 *  classroom — its network responses must never carry that secret. */
export interface KioskRosterEntry {
  id: string;
  class_id: string;
  display_name: string;
  student_id: string | null;
  claimed_at: string | null;
  /** MA-925 — the persisted door picture (migration 0059, lowest free in the
   *  class, never reassigned). Absent (undefined) before the founder applies
   *  0059: the door then shows today's initial tile. */
  door_picture?: number | null;
}

export async function fetchClassRosterForKiosk(
  classId: string,
  client: SupabaseClient = supabase,
): Promise<KioskRosterEntry[]> {
  // MA-925 — door_picture is selected first; before the founder applies 0059
  // PostgREST answers 42703 for the unknown column, and the read retries once
  // without it (the established isMissingColumnError convention), so the door
  // still loads with today's initial tiles. Any other error throws, and the
  // door shows its honest "Can't load the class list" state.
  // MA-939 — removed_at is selected too (migration 0065) and a removed child is
  // dropped from the door below; before 0065 is applied PostgREST answers 42703
  // naming removed_at and the read retries without it (the same convention),
  // so the door reads as today. The armed device's next roster read then
  // prunes the removed child from the device (MA-925).
  const read = (columns: string) =>
    client
      .from("class_enrollments")
      .select(columns)
      .eq("class_id", classId)
      .order("joined_at", { ascending: true });
  let withPicture = true;
  let withRemoved = true;
  const columnsNow = () =>
    [KIOSK_ROSTER_COLUMNS, withPicture ? "door_picture" : null, withRemoved ? "removed_at" : null].filter(Boolean).join(", ");
  let { data, error } = await read(columnsNow());
  for (let retry = 0; retry < 2 && error; retry += 1) {
    if (withRemoved && isMissingNamedColumnError(error, "removed_at")) withRemoved = false;
    else if (withPicture && isMissingColumnError(error, "door_picture")) withPicture = false;
    else break;
    ({ data, error } = await read(columnsNow()));
  }
  if (error) {
    captureSupabaseError("fetchClassRosterForKiosk failed", error, { classId });
    throw error;
  }
  return ((data ?? []) as unknown as (KioskRosterEntry & { removed_at?: string | null })[])
    .filter((row) => !isRemovedEnrollment(row))
    .map(({ removed_at: _removedAt, ...row }) => row);
}

const KIOSK_ROSTER_COLUMNS = "id, class_id, display_name, student_id, claimed_at";

/** MA-288 kid-secret sub-slice — the ONE column the kiosk door needs from
 *  `classes` before rendering a tile. Narrowed (not `select("*")`) for the
 *  same reason `fetchClassRosterForKiosk` narrows its own select: a kiosk
 *  device's network responses should carry exactly what the door needs and
 *  nothing else. Pre-apply (0042 not yet live), `kid_secret_required`
 *  doesn't exist as a column — PostgREST 400s with 42703 — so this falls
 *  back to `{ kid_secret_required: false }` (the DEFAULT the migration
 *  itself will set once applied): the picture-secret gate simply doesn't
 *  show yet, matching every other 42703/PGRST202 pre-apply fallback in this
 *  file. */
export async function fetchKioskClassMeta(
  classId: string,
  client: SupabaseClient = supabase,
): Promise<{ id: string; kid_secret_required: boolean }> {
  const { data, error } = await client
    .from("classes")
    .select("id, kid_secret_required")
    .eq("id", classId)
    .single();
  if (error) {
    if (isMissingColumnError(error, "kid_secret_required")) {
      return { id: classId, kid_secret_required: false };
    }
    captureSupabaseError("fetchKioskClassMeta failed", error, { classId });
    throw error;
  }
  return (data as { id: string; kid_secret_required: boolean } | null) ?? { id: classId, kid_secret_required: false };
}

/** MA-925 (D-1012-29) — the armed class's teacher id, as THIS session can
 *  see it. `classes` is RLS-scoped to `teacher_id = auth.uid()`, so a
 *  signed-out device, a parent or another teacher gets no row (null). The door
 *  prunes local kiosk children only when this equals the signed-in user id.
 *  Never throws: any error is "cannot tell" (null), so nothing is deleted. */
export async function fetchKioskClassTeacherId(
  classId: string,
  client: SupabaseClient = supabase,
): Promise<string | null> {
  try {
    const { data, error } = await client
      .from("classes")
      .select("id, teacher_id")
      .eq("id", classId)
      .maybeSingle();
    if (error || !data) return null;
    const t = (data as { teacher_id?: unknown }).teacher_id;
    return typeof t === "string" && t.length > 0 ? t : null;
  } catch {
    return null;
  }
}

/** MA-542 — the kiosk-shadow read the teacher analytics layers need: every
 *  UNCLAIMED enrollment's shadow `students` row (migration 0042's
 *  `students.enrollment_id`), keyed by `enrollment_id` so a caller can
 *  resolve `analyticsStudentId = row.student_id ?? shadowMap.get(row.id) ??
 *  null` for every roster row without a second per-row round trip.
 *
 *  RLS already scopes this to the caller's OWN shadow rows
 *  (`parent_id = auth.uid()` is the signed-in teacher for every shadow —
 *  see 0042's header) — the `.not("enrollment_id", "is", null)` filter here
 *  is a network-payload narrowing (don't ship a teacher's own family-kid
 *  rows to this call site, which has no use for them), not the security
 *  boundary; the real boundary is Postgres RLS, exactly like every other
 *  read in this file.
 *
 *  Pre-apply (0042 not yet founder-applied): `enrollment_id` doesn't exist
 *  as a column yet, so PostgREST 400s with 42703 — this falls back to an
 *  EMPTY map, the same fail-closed shape `fetchKioskClassMeta` uses above.
 *  An empty map collapses `analyticsStudentId` to `row.student_id ?? null`
 *  for every row at every call site — byte-identical to today's
 *  linked-only behavior until 0042 ships. */
export async function fetchKioskShadowMap(
  client: SupabaseClient = supabase,
): Promise<Map<string, string>> {
  const { data, error } = await client
    .from("students")
    .select("id, enrollment_id")
    .not("enrollment_id", "is", null);
  if (error) {
    if (isMissingColumnError(error, "enrollment_id")) return new Map();
    captureSupabaseError("fetchKioskShadowMap failed", error, {});
    throw error;
  }
  const map = new Map<string, string>();
  for (const row of (data ?? []) as { id: string; enrollment_id: string | null }[]) {
    if (row.enrollment_id) map.set(row.enrollment_id, row.id);
  }
  return map;
}

/** MA-436 class-progress-view compose slice — reads the EXISTING
 *  `students.assigned_skill_ids` column (migration 0027, already applied —
 *  see docs/PROD_APPLIED.md) for an already-scoped set of student ids, so
 *  the "ready to advance" derivation (readyToAdvance.ts) can tell a student
 *  who is secure on their focus skill FROM a student who's secure AND
 *  already has a next skill queued. No new column, no new table — this
 *  reuses `assign_skill_to_student`'s own write target (assignApi.ts) as a
 *  read.
 *
 *  RLS (`students_select`, 0000 baseline) already permits this for exactly
 *  the id set every other analytics read on this screen uses:
 *  `parent_id = auth.uid()` (a teacher's own kiosk-shadow rows, MA-542) OR
 *  `student_access.granted_to = auth.uid()` (a claimed student's teacher —
 *  the grant `claim_class_enrollment`, migration 0026, mints). Callers MUST
 *  pass only the same consent-filtered id set the heatmap/dosage strip
 *  already use (consentedLinkedIds + shadowIds) — this function performs no
 *  consent check itself, same contract as useClassMasteryStates. */
export async function fetchAssignedSkillIdsForStudents(
  studentIds: string[],
  client: SupabaseClient = supabase,
): Promise<Record<string, string[]>> {
  if (studentIds.length === 0) return {};
  const { data, error } = await client
    .from("students")
    .select("id, assigned_skill_ids")
    .in("id", studentIds);
  if (error) {
    if (isMissingColumnError(error, "assigned_skill_ids")) return {};
    captureSupabaseError("fetchAssignedSkillIdsForStudents failed", error, { studentIds });
    throw error;
  }
  const out: Record<string, string[]> = {};
  for (const row of (data ?? []) as { id: string; assigned_skill_ids?: unknown }[]) {
    out[row.id] = Array.isArray(row.assigned_skill_ids)
      ? row.assigned_skill_ids.filter((s): s is string => typeof s === "string")
      : [];
  }
  return out;
}

/** Teacher-only: flips the per-class picture-secret gate. DEVIATION NOTE
 *  (see 0042's own header + this PR's final report): 0023 granted only
 *  SELECT, INSERT on `classes` — no UPDATE grant or RLS UPDATE policy
 *  existed at all before this slice, for ANY column, teacher or otherwise.
 *  0042 adds the narrowest authorization that could make this call work: a
 *  COLUMN-level `GRANT UPDATE (kid_secret_required)` (every other column —
 *  name, join_code, archived, teacher_id — stays ungrantable via this
 *  path) plus a `classes_update_kid_secret` RLS policy scoped to
 *  `teacher_id = auth.uid()`, the same ownership predicate every other
 *  classes/class_enrollments policy already uses. This is a SAME-TENANT
 *  grant (a teacher updating their own class row) — not the cross-tenant
 *  case the shadow-student/RPC design above exists to avoid. */
export async function updateClassKidSecretRequired(
  classId: string,
  required: boolean,
  client: SupabaseClient = supabase,
): Promise<void> {
  const { error } = await client.from("classes").update({ kid_secret_required: required }).eq("id", classId);
  if (error) {
    captureSupabaseError("updateClassKidSecretRequired failed", error, { classId, required });
    throw error;
  }
}

/** Inserts one `class_enrollments` row per name, `student_id: null` (see the
 *  module header — real learner rows are a follow-up sub-slice). */
export async function addRosterNames(
  classId: string,
  names: string[],
  client: SupabaseClient = supabase,
): Promise<ClassEnrollment[]> {
  const { data, error } = await client
    .from("class_enrollments")
    .insert(names.map((display_name) => ({ class_id: classId, display_name, student_id: null })))
    .select();
  if (error) {
    captureSupabaseError("addRosterNames failed", error, { classId, count: names.length });
    throw error;
  }
  return (data ?? []) as ClassEnrollment[];
}

export interface ConsentRow {
  granted: boolean;
  revoked_at: string | null;
  consent_type: string;
}

/** MA-60 lane (b) — the real gated chokepoint `RosterPaste` calls. Parses the
 *  textarea, then runs the school-authority consent gate FRESH (a re-check at
 *  submit time, not just the earlier render-time fetch that decided whether to
 *  show the roster form) before ever calling `addRosterNames`. Structured as a
 *  plain DI-friendly async function (not inline in the component) so it is
 *  directly unit-testable without a DOM/render harness, per this repo's
 *  pure-core convention — the same rigor `runGatedRosterWrite`'s own bite test
 *  applies, extended through to the ACTUAL names-parsing + insert call. */
export async function submitRosterNames({
  classId,
  teacherId,
  text,
  existingNames = [],
  addAnyway = [],
  devBypass,
  fetchConsent,
  insertFn = addRosterNames,
}: {
  classId: string;
  teacherId: string;
  text: string;
  /** MA-926 — the class's current roster names: a paste never re-adds them. */
  existingNames?: readonly string[];
  /** MA-926 (D-1013-18) — entries the teacher chose to "Add as" a longer form. */
  addAnyway?: readonly string[];
  devBypass: boolean;
  fetchConsent: (teacherId: string) => Promise<ConsentRow[]>;
  insertFn?: (classId: string, names: string[]) => Promise<ClassEnrollment[]>;
}): Promise<{ rows: ClassEnrollment[] | null; blocked: boolean }> {
  const names = parseRosterNames(text, existingNames, addAnyway);
  if (names.length === 0) return { rows: null, blocked: false };
  let blocked = false;
  const rows = await runGatedRosterWrite({
    classId,
    teacherId,
    devBypass,
    fetchConsent,
    insert: () => insertFn(classId, names),
    onBlocked: () => {
      blocked = true;
    },
  });
  return { rows: rows as ClassEnrollment[] | null, blocked };
}

// ── MA-926 (D-TUX-3) — rename and move (migration 0061, founder applies) ──────

/** The live state of 0061 as this session can tell without changing
 *  anything: "live" (the move RPC exists), "not-live" (PostgREST answers a
 *  missing function, PGRST202 / 42883 — the pre-apply state), "unknown" (any
 *  other answer, e.g. offline; the controls stay usable and a real failure
 *  shows its own message). */
export type RosterEditLive = "live" | "not-live" | "unknown";

const NIL_UUID = "00000000-0000-0000-0000-000000000000";

/** True for the pre-apply answers of a missing RPC: PostgREST's PGRST202
 *  (the established claimApi.isMissingFunctionError convention) or the bare
 *  Postgres 42883 "function does not exist". */
export function isMissingMoveFunctionError(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null | undefined;
  return (
    e?.code === "PGRST202" ||
    e?.code === "42883" ||
    /Could not find the function|function .*move_class_enrollment.* does not exist/i.test(e?.message ?? "")
  );
}

/** Asks PostgREST for the move RPC with a GET (the read-only census allows
 *  GET/HEAD only) and the nil id for both arguments. PostgREST runs a GET
 *  call in a READ ONLY transaction, so nothing can be written: once 0061 is
 *  applied the call fails inside the function (`not_owner`, or the read-only
 *  transaction refusing its row lock, 25006) — a Postgres error, so the
 *  function exists; before, PostgREST answers a missing function. Never
 *  writes; never reports to Sentry. */
export async function probeRosterEditLive(client: SupabaseClient = supabase): Promise<RosterEditLive> {
  try {
    const { error } = await client.rpc(
      "move_class_enrollment",
      { p_enrollment_id: NIL_UUID, p_target_class_id: NIL_UUID },
      { get: true },
    );
    if (!error) return "live";
    if (isMissingMoveFunctionError(error)) return "not-live";
    const code = (error as { code?: string }).code ?? "";
    // A Postgres SQLSTATE came back from inside the function: it exists.
    return /^[0-9A-Z]{5}$/.test(code) && !code.startsWith("PGRST") ? "live" : "unknown";
  } catch {
    return "unknown";
  }
}

/** Why a move was refused, from the RPC's own exception text. */
export type MoveRefusal = "class_ack_required" | "not_owner" | "bad_target" | "not_live" | "failed";

export function moveRefusalOf(error: unknown): MoveRefusal {
  if (isMissingMoveFunctionError(error)) return "not_live";
  const msg = (error as { message?: string } | null | undefined)?.message ?? "";
  if (/class_ack_required/.test(msg)) return "class_ack_required";
  if (/not_owner/.test(msg)) return "not_owner";
  if (/bad_target/.test(msg)) return "bad_target";
  return "failed";
}

/** True when a rename was refused because 0061 is not applied: the
 *  authenticated role has no UPDATE privilege on class_enrollments at all
 *  (0055), so PostgREST answers 42501. */
export function isRenameNotLiveError(error: unknown): boolean {
  return (error as { code?: string } | null | undefined)?.code === "42501";
}

/** Renames one roster row (display_name only — the column grant 0061 adds) and,
 *  best effort, the teacher's own kiosk shadow of that row (`students` with
 *  `enrollment_id` = the row and `parent_id` = the teacher, so a linked
 *  family's child is never renamed). Returns the row's new name. */
export async function renameEnrollment(
  enrollmentId: string,
  displayName: string,
  teacherId: string,
  client: SupabaseClient = supabase,
): Promise<{ id: string; display_name: string }> {
  const { data, error } = await client
    .from("class_enrollments")
    .update({ display_name: displayName })
    .eq("id", enrollmentId)
    .select("id, display_name")
    .single();
  if (error) {
    if (!isRenameNotLiveError(error)) captureSupabaseError("renameEnrollment failed", error, { enrollmentId });
    throw error;
  }
  const shadow = await client
    .from("students")
    .update({ name: displayName })
    .eq("enrollment_id", enrollmentId)
    .eq("parent_id", teacherId);
  if (shadow.error && !isMissingColumnError(shadow.error, "enrollment_id")) {
    // The roster row is renamed; the door reads display_name from it. A shadow
    // that keeps its old name is reported, not shown to the teacher as a failure.
    captureSupabaseError("renameEnrollment shadow rename failed", shadow.error, { enrollmentId });
  }
  return data as { id: string; display_name: string };
}

/** Moves one roster row to another class the teacher owns through the
 *  SECURITY DEFINER RPC (0061), which re-checks ownership of both classes and
 *  the target class's roster acknowledgement. Returns the moved row's id,
 *  class and new door picture. */
export async function moveEnrollment(
  enrollmentId: string,
  targetClassId: string,
  client: SupabaseClient = supabase,
): Promise<{ id: string; class_id: string; door_picture: number | null }> {
  const { data, error } = await client.rpc("move_class_enrollment", {
    p_enrollment_id: enrollmentId,
    p_target_class_id: targetClassId,
  });
  if (error) {
    const why = moveRefusalOf(error);
    if (why === "failed") captureSupabaseError("moveEnrollment failed", error, { enrollmentId, targetClassId });
    throw error;
  }
  const row = (Array.isArray(data) ? data[0] : data) as
    | { id: string; class_id: string; door_picture: number | null }
    | undefined;
  if (!row) throw new Error("move_class_enrollment returned no row");
  return row;
}

// ── MA-929 (D-TUX-5) — archive a class (migration 0062, founder applies) ──────

/** The classes a teacher's switcher lists and opens: every class not archived. */
export function liveClasses<T extends { archived?: boolean | null }>(rows: readonly T[]): T[] {
  return rows.filter((row) => row.archived !== true);
}

/** The live state of 0062 as this session can tell without changing anything:
 *  "live" (class_archive_live() answers true), "not-live" (PostgREST answers a
 *  missing function, PGRST202 / 42883, or the function answers false — the
 *  pre-apply state), "unknown" (any other answer, e.g. offline; Archive stays
 *  usable and a refused write shows the not-live message itself). */
export type ArchiveLive = "live" | "not-live" | "unknown";

/** True for the answers of an archive write or probe before 0062 is applied:
 *  42501 (no UPDATE privilege on classes.archived), the 42703 column pattern
 *  (isMissingColumnError) and a missing probe function (PGRST202 / 42883). */
export function isArchiveNotLiveError(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null | undefined;
  return (
    e?.code === "42501" ||
    e?.code === "PGRST202" ||
    e?.code === "42883" ||
    isMissingColumnError(error, "archived") ||
    /Could not find the function|function .*class_archive_live.* does not exist/i.test(e?.message ?? "")
  );
}

/** Asks PostgREST for class_archive_live() with a GET (the read-only census
 *  allows GET/HEAD only; PostgREST runs a GET call in a READ ONLY transaction).
 *  The function only reads the caller's UPDATE privilege on classes.archived.
 *  Never writes; never reports to Sentry. */
export async function probeClassArchiveLive(client: SupabaseClient = supabase): Promise<ArchiveLive> {
  try {
    const { data, error } = await client.rpc("class_archive_live", {}, { get: true });
    if (!error) return data === true ? "live" : "not-live";
    return isArchiveNotLiveError(error) ? "not-live" : "unknown";
  } catch {
    return "unknown";
  }
}

/** Archives one class the caller owns (archived only — the column grant 0062
 *  adds; RLS scopes it to the owning teacher). Throws the PostgREST error; a
 *  not-live answer is not reported to Sentry.
 *  MA-940: `retentionNotice: true` — sent ONLY by a confirm that showed the
 *  retention sentence — also writes archive_notice_shown (0066's column grant)
 *  in the same write; without it the write is exactly 0062's. */
export async function archiveClass(
  classId: string,
  client: SupabaseClient = supabase,
  opts?: { retentionNotice?: boolean },
): Promise<{ id: string; archived: boolean }> {
  const { data, error } = await client
    .from("classes")
    .update(opts?.retentionNotice === true ? { archived: true, archive_notice_shown: true } : { archived: true })
    .eq("id", classId)
    .select("id, archived")
    .single();
  if (error) {
    if (!isArchiveNotLiveError(error)) captureSupabaseError("archiveClass failed", error, { classId });
    throw error;
  }
  return data as { id: string; archived: boolean };
}

/** MA-929 — whether the armed class has been archived, as THIS session can see
 *  it (narrowed: id and archived only; `archived` exists since 0023). RLS
 *  scopes `classes` to its teacher, so a signed-out or foreign session gets no
 *  row: null ("cannot tell"), and the door stays as it is. Never throws. */
export async function fetchKioskClassArchived(
  classId: string,
  client: SupabaseClient = supabase,
): Promise<boolean | null> {
  try {
    const { data, error } = await client.from("classes").select("id, archived").eq("id", classId).maybeSingle();
    if (error || !data) return null;
    const archived = (data as { archived?: unknown }).archived;
    return typeof archived === "boolean" ? archived : null;
  } catch {
    return null;
  }
}

// ── MA-936 (D-TUX-24) — a linked child's in-class lane (migration 0063, founder applies) ──

/** The pre-apply answers of 0063: a missing function (PostgREST PGRST202, bare
 *  Postgres 42883) or a missing column (42703). */
export function isClassLaneAbsentError(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null | undefined;
  return (
    e?.code === "PGRST202" ||
    e?.code === "42883" ||
    e?.code === "42703" ||
    /Could not find the function|function .*teacher_linked_students.* does not exist/i.test(e?.message ?? "")
  );
}

/** The teacher's "ready for next" signal for each linked child of one class,
 *  through 0063's SECURITY DEFINER `teacher_linked_students(p_class_id)` (a GET:
 *  read-only), so the teacher area never reads the family's own queue. Returns
 *  the lane state (src/lib/teacher/classLane.ts): "absent" before 0063 is
 *  applied (every view then reads as on main), "live" with has_queued_skill per
 *  linked child, or "unknown" on any other answer, which fails closed (no tier
 *  word, a neutral dosage placeholder, not Ready for next; D-1017-45). Never
 *  throws. */
export async function fetchTeacherClassLane(
  classId: string,
  client: SupabaseClient = supabase,
  /** RED r2 S1: the roster's linked child ids and the signed-in teacher. A
   *  linked child the RPC does not return is "owned" only when the teacher's
   *  own students row for it is read back with parent_id = teacherId. */
  ownership?: { linkedStudentIds: readonly string[]; teacherId: string | null },
): Promise<TeacherClassLane> {
  try {
    const { data, error } = await client.rpc("teacher_linked_students", { p_class_id: classId }, { get: true });
    if (error) {
      if (isClassLaneAbsentError(error)) return { state: "absent" };
      captureSupabaseError("fetchTeacherClassLane failed", error, { classId });
      return { state: "unknown" };
    }
    const hasQueuedByStudent: Record<string, boolean> = {};
    for (const row of (Array.isArray(data) ? data : []) as { student_id?: unknown; has_queued_skill?: unknown }[]) {
      if (typeof row.student_id === "string") hasQueuedByStudent[row.student_id] = row.has_queued_skill === true;
    }
    const missing = (ownership?.linkedStudentIds ?? []).filter((id) => !(id in hasQueuedByStudent));
    const ownedStudentIds: string[] = [];
    if (missing.length > 0 && ownership?.teacherId) {
      const owned = await client.from("students").select("id, parent_id").in("id", missing);
      if (!owned.error) {
        for (const row of (owned.data ?? []) as { id?: unknown; parent_id?: unknown }[]) {
          if (typeof row.id === "string" && row.parent_id === ownership.teacherId) ownedStudentIds.push(row.id);
        }
      }
    }
    return { state: "live", hasQueuedByStudent, ownedStudentIds };
  } catch {
    return { state: "unknown" };
  }
}

// ── MA-939 (D-TUX-29) — remove a child from the class list (migration 0065, founder applies) ──

/** The live state of 0065 as this session can tell without changing anything:
 *  "live" (the remove RPC exists), "not-live" (PostgREST answers a missing
 *  function, PGRST202 / 42883 — the pre-apply state), "unknown" (any other
 *  answer, e.g. offline). The Students tab shows Remove from class only when
 *  "live". */
export type RosterRemoveLive = "live" | "not-live" | "unknown";

/** MA-939 RED r1 N2 — the RPC's refusal of a row that is already removed (another
 *  tab removed it first). The row is off the class list either way, so the
 *  caller treats it as done. */
export function isAlreadyRemovedError(error: unknown): boolean {
  return /already_removed/.test((error as { message?: string } | null | undefined)?.message ?? "");
}

/** True for the pre-apply answers of the remove RPC: PGRST202 or 42883. */
export function isMissingRemoveFunctionError(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null | undefined;
  return (
    e?.code === "PGRST202" ||
    e?.code === "42883" ||
    /Could not find the function|function .*remove_class_enrollment.* does not exist/i.test(e?.message ?? "")
  );
}

/** Asks PostgREST for the remove RPC with a GET (the read-only census allows
 *  GET/HEAD only) and the nil id. PostgREST runs a GET call in a READ ONLY
 *  transaction, so nothing can be written: once 0065 is applied the call fails
 *  inside the function (`not_owner`, or the read-only transaction refusing its
 *  row lock, 25006) — a Postgres error, so the function exists; before, PostgREST
 *  answers a missing function. Never writes; never reports to Sentry. */
export async function probeRosterRemoveLive(client: SupabaseClient = supabase): Promise<RosterRemoveLive> {
  try {
    const { error } = await client.rpc("remove_class_enrollment", { p_enrollment_id: NIL_UUID }, { get: true });
    if (!error) return "live";
    if (isMissingRemoveFunctionError(error)) return "not-live";
    const code = (error as { code?: string }).code ?? "";
    return /^[0-9A-Z]{5}$/.test(code) && !code.startsWith("PGRST") ? "live" : "unknown";
  } catch {
    return "unknown";
  }
}

/** Removes one roster row from the class list through the SECURITY DEFINER RPC
 *  (0065), which re-checks ownership, stamps removed_at, revokes the teacher's
 *  grant on a linked child and retires an unclaimed child's shadow. There is no
 *  undo. Throws the PostgREST error; a not-live answer is not reported. */
export async function removeEnrollment(
  enrollmentId: string,
  client: SupabaseClient = supabase,
  /** MA-940: `retentionNotice: true` — sent ONLY by a confirm that showed the
   *  retention sentence — adds p_retention_notice (0066); without it the call
   *  is exactly 0065's one-argument call. */
  opts?: { retentionNotice?: boolean },
): Promise<void> {
  const args =
    opts?.retentionNotice === true
      ? { p_enrollment_id: enrollmentId, p_retention_notice: true }
      : { p_enrollment_id: enrollmentId };
  const { error } = await client.rpc("remove_class_enrollment", args);
  if (error) {
    if (!isMissingRemoveFunctionError(error) && !isAlreadyRemovedError(error)) {
      captureSupabaseError("removeEnrollment failed", error, { enrollmentId });
    }
    throw error;
  }
}

// ── MA-940 (D-TUX-29) — the retention sentence and its notice markers (migration 0066, founder applies) ──

/** MA-940 — the sentence the Remove and Archive confirms gain, byte-identical
 *  to its docs/legal/PENDING-COPY.md MA-940 DRAFT entries. Shown only when
 *  `schoolRetentionNoticeLive` says so. */
export const SCHOOL_RETENTION_SENTENCE =
  "If their family hasn't linked, their in-class practice is deleted 60 days after this.";

/** The raw VITE_RETENTION_SCHOOL_WINDOW_LIVE value (undefined outside Vite,
 *  e.g. the node/tsx test runner, where import.meta.env does not exist). */
function readSchoolRetentionEnv(): string | undefined {
  const env =
    typeof import.meta !== "undefined"
      ? (import.meta as unknown as { env?: Record<string, unknown> }).env
      : undefined;
  const v = env?.VITE_RETENTION_SCHOOL_WINDOW_LIVE;
  return typeof v === "string" ? v : undefined;
}

/** MA-940 — the client half of the flag the retention cron's school-lane
 *  window also requires (the founder sets and unsets it together with
 *  RETENTION_PURGE_ARMED, docs/ops/FOUNDER-FYI.md). Exactly "true" only. */
export function isSchoolRetentionFlagOn(value: string | undefined = readSchoolRetentionEnv()): boolean {
  return value === "true";
}

/** Whether 0066's marker column(s) exist, as this session can tell with one
 *  GET that returns no rows: "live" (the read succeeds), "absent" (42703, the
 *  pre-apply state), "unknown" (anything else: the sentence stays hidden). */
export type RetentionColumnsLive = "live" | "absent" | "unknown";

async function probeColumns(client: SupabaseClient, table: string, columns: string): Promise<RetentionColumnsLive> {
  try {
    const { error } = await client.from(table).select(columns).limit(0);
    if (!error) return "live";
    return (error as { code?: string }).code === "42703" ? "absent" : "unknown";
  } catch {
    return "unknown";
  }
}

/** MA-940 — does class_enrollments.removal_notice_shown exist? Never writes;
 *  never reports to Sentry. Called only while the flag is on. */
export function probeRemovalNoticeColumn(client: SupabaseClient = supabase): Promise<RetentionColumnsLive> {
  return probeColumns(client, "class_enrollments", "removal_notice_shown");
}

/** MA-940 — do classes.archived_at and classes.archive_notice_shown exist?
 *  Never writes; never reports to Sentry. Called only while the flag is on. */
export function probeArchiveNoticeColumns(client: SupabaseClient = supabase): Promise<RetentionColumnsLive> {
  return probeColumns(client, "classes", "archived_at, archive_notice_shown");
}

/** MA-940 — the one rule for showing the sentence (and so sending the marker):
 *  the flag is "true" AND the columns answered. */
export function schoolRetentionNoticeLive(flagOn: boolean, columns: RetentionColumnsLive): boolean {
  return flagOn && columns === "live";
}
